/**
 * Contratto del bridge wrapper React Native <-> PWA WebView.
 *
 * I messaggi vengono inviati dalla PWA al wrapper via
 * `window.ReactNativeWebView.postMessage(JSON.stringify(msg))`.
 * Il wrapper, a sua volta, invia a PWA via `webRef.injectJavaScript(...)`
 * con `vvfNativeBridge.handle*` oppure dispatchando CustomEvent globali.
 *
 * Tutti i tipi sono qui per essere importati sia dal codice mobile
 * (`apps/mobile/src/hooks/useBridgeMessages.ts`) sia dal codice PWA
 * (`apps/web/src/services/nativeBridge.ts`). Un cambio di nome evento
 * o di shape diventa errore TypeScript in entrambi i posti.
 */

// ============================================================================
// PWA -> Native (postMessage)
// ============================================================================

export type AuthLoginMessage = {
  type: 'auth:login';
  accessToken: string;
  refreshToken: string;
  user: Record<string, unknown>;
};

export type AuthLogoutMessage = { type: 'auth:logout' };

/**
 * Item #1 (single-owner refresh): la PWA chiede al NATIVO (owner unico del
 * refresh in wrapper) di rinnovare i token. Il nativo esegue /auth/refresh e
 * sincronizza i nuovi token alla PWA via registerTokenRefreshSync (che scrive
 * vvf.access_token/refresh_token in localStorage). La PWA NON chiama mai
 * /auth/refresh in wrapper, eliminando la race sul refresh token che ruota.
 */
export type AuthRequestRefreshMessage = { type: 'auth:request-refresh' };

export type AuthUserUpdateMessage = {
  type: 'auth:user-update';
  user: Record<string, unknown>;
};

export type SelectiveAcceptMessage = {
  type: 'selective:accept';
  selectiveId: string;
};

export type SelectiveRejectMessage = {
  type: 'selective:reject';
  selectiveId: string;
};

export type GpsStartTrackingMessage = {
  type: 'gps:start-tracking';
  selectiveId: string;
};

export type GpsStopTrackingMessage = { type: 'gps:stop-tracking' };
export type GpsCheckEnabledMessage = { type: 'gps:check-enabled' };
export type GpsOpenSettingsMessage = { type: 'gps:open-settings' };

export type DebugTestAlarmMessage = { type: 'debug:test-alarm' };
export type DebugDumpVolumesMessage = { type: 'debug:dump-volumes' };
export type DebugGetLastBgDumpMessage = { type: 'debug:get-last-bg-dump' };

export type DndCheckMessage = { type: 'dnd:check' };
export type DndOpenSettingsMessage = { type: 'dnd:open-settings' };
export type DndSetPreferenceMessage = {
  type: 'dnd:set-preference';
  enable: boolean;
};

export type AudioSetBypassVolumeMessage = {
  type: 'audio:set-bypass-volume';
  enable: boolean;
};

// Bryan: audio ownership unificata. La PWA dentro wrapper non produce
// piu' audio (no doppia sirena). Chiama questi messaggi e il wrapper
// suona i suoni nativi built-in (gli audio personalizzati sono stati
// rimossi: i suoni sono sempre quelli built-in, browser e app).
export type AudioPlaySelectiveAlarmMessage = { type: 'audio:play-selective-alarm' };
export type AudioStopSelectiveAlarmMessage = { type: 'audio:stop-selective-alarm' };
export type AudioPlayEndChimeMessage = { type: 'audio:play-end-chime' };
export type AudioStopEndChimeMessage = { type: 'audio:stop-end-chime' };

export type LogMessage = {
  type: 'log';
  payload: unknown;
};

/**
 * Upload documenti nativo: nel WebView l'upload multipart di un file scelto
 * con <input type=file> non funziona in modo affidabile su Android (il file
 * arriva come content:// non serializzabile). La PWA chiede quindi al wrapper
 * di gestire selezione + upload nativamente (expo-document-picker + fetch con
 * file:// leggibile). A fine upload il nativo notifica la PWA (evento
 * DocumentUploaded) che ricarica la lista.
 */
export type FilePickUploadMessage = {
  type: 'file:pick-upload';
  /** Categoria documento (ODG | POS | Manuali). */
  category: string;
};

/**
 * Scatto/scelta FOTO nativa (scheda Rifornimenti scontrini): il wrapper apre
 * fotocamera o galleria (expo-image-picker) e carica l'immagine sull'endpoint
 * documenti per la categoria data. A differenza di file:pick-upload (selettore
 * file generico), questo serve a "scattare lo scontrino" al volo.
 */
export type FilePickPhotoMessage = {
  type: 'file:pick-photo';
  /** Categoria documento (tipicamente "Rifornimenti"). */
  category: string;
  /** Nome custom opzionale per il file (es. "ranger 22/10/2026"). Vuoto = auto. */
  filename?: string;
};

/**
 * Avvia una chiamata: la PWA chiede al wrapper di aprire il dialer nativo.
 * Necessario perche' un `<a href="tel:">` nel WebView su Android spesso non
 * apre il dialer (onShouldStartLoadWithRequest inaffidabile per tel:). Il
 * numero deve essere gia' sanitizzato (solo cifre e +).
 */
export type TelMessage = {
  type: 'tel';
  number: string;
};

/**
 * #2: la PWA dentro il WebView NON puo' scaricare file (i download blob del
 * browser vengono ignorati dal WebView). Per export Excel/CSV/TXT, PDF, download
 * documenti, la PWA invia il file in base64 al wrapper, che lo salva su disco
 * (expo-file-system) e apre il foglio di condivisione/salvataggio nativo
 * (expo-sharing). Nel browser normale resta il download classico.
 */
export type FileSaveMessage = {
  type: 'file:save';
  filename: string;
  /** MIME del file (es. xlsx, application/pdf, text/csv). */
  mimeType: string;
  /** Contenuto del file codificato base64 (senza prefisso data:). */
  base64: string;
};

/**
 * "Visualizza" documento dentro l'app nativa. Nel WebView `window.open(blob:…)`
 * non apre nulla, quindi la PWA invia il file in base64 e il wrapper lo apre
 * DIRETTAMENTE con il visualizzatore di sistema (Android: intent VIEW su
 * content:// via FileProvider; fallback al foglio di condivisione). A
 * differenza di `file:save`, l'intento e' APRIRE (lettura), non salvare.
 */
export type FileOpenMessage = {
  type: 'file:open';
  filename: string;
  /** MIME del file (es. application/pdf, image/png). */
  mimeType: string;
  /** Contenuto del file codificato base64 (senza prefisso data:). */
  base64: string;
};

/** Discriminated union di tutti i messaggi PWA -> wrapper. */
export type BridgeMessage =
  | AuthLoginMessage
  | AuthLogoutMessage
  | AuthRequestRefreshMessage
  | AuthUserUpdateMessage
  | SelectiveAcceptMessage
  | SelectiveRejectMessage
  | GpsStartTrackingMessage
  | GpsStopTrackingMessage
  | GpsCheckEnabledMessage
  | GpsOpenSettingsMessage
  | DebugTestAlarmMessage
  | DebugDumpVolumesMessage
  | DebugGetLastBgDumpMessage
  | DndCheckMessage
  | DndOpenSettingsMessage
  | DndSetPreferenceMessage
  | AudioSetBypassVolumeMessage
  | AudioPlaySelectiveAlarmMessage
  | AudioStopSelectiveAlarmMessage
  | AudioPlayEndChimeMessage
  | AudioStopEndChimeMessage
  | FileSaveMessage
  | FileOpenMessage
  | FilePickUploadMessage
  | FilePickPhotoMessage
  | TelMessage
  | LogMessage;

export type BridgeMessageType = BridgeMessage['type'];

// ============================================================================
// Native -> PWA (CustomEvent dispatched via injectJavaScript)
// ============================================================================

/** Eventi che il wrapper nativo dispatcha sul `window` della PWA. */
export const NATIVE_EVENT = {
  /** Utente ha premuto "Accetto" sul modal nativo. detail: { selectiveId } */
  Accept: 'vvf:native:accept',
  /** Utente ha premuto "Non accetto" sul modal nativo. detail: { selectiveId } */
  Reject: 'vvf:native:reject',
  /** Risultato del check permesso DnD. detail: { granted: boolean } */
  DndResult: 'vvf:native:dnd-result',
  /** Risultato del check GPS hardware. detail: { enabled: boolean } */
  GpsEnabled: 'vvf:native:gps-enabled',
  /**
   * Esito di startBackgroundGps (audit #4). detail: { active: boolean, reason?: string }.
   * reason: 'no-fg-permission' | 'no-bg-permission' | 'error' quando active=false.
   * Permette alla PWA di mostrare warning all'utente che il tracking non e' partito.
   */
  GpsTrackingStatus: 'vvf:native:gps-tracking-status',
  /**
   * Risultato di debug:dump-volumes. detail: NativeVolumesDumpDetail con
   * stato flussi audio + ringerMode + permission DnD. Permette alla PWA di
   * mostrare i valori in un dialog senza dover usare adb logcat / chrome://inspect.
   */
  VolumesDump: 'vvf:native:volumes-dump',
  /** Ultimo snapshot diagnostico catturato all'arrivo FCM (anche in bg).
   *  detail: NativeLastBgDumpDetail con ts ISO + i campi audio + appState. */
  LastBgDump: 'vvf:native:last-bg-dump',
  /**
   * Item #5: il nativo ha sincronizzato i nuovi token in localStorage (dopo
   * auth:request-refresh). Sostituisce il busy-poll lato PWA: l'interceptor
   * attende questo evento per rileggere l'access token rinnovato.
   * detail: NativeTokensSyncedDetail { accessToken }.
   */
  TokensSynced: 'vvf:native:tokens-synced',
  /**
   * Il refresh delegato al nativo (dopo auth:request-refresh) e' FALLITO. La
   * PWA usa il `kind` per decidere: 'auth' = sessione invalida -> login;
   * 'network' = errore transitorio -> NON sloggare, resta in sessione.
   * detail: NativeTokenRefreshFailedDetail { kind }.
   */
  TokenRefreshFailed: 'vvf:native:token-refresh-failed',
  /** Il nativo ha completato un upload documento (file:pick-upload) -> la PWA
   *  ricarica la lista documenti. Nessun detail. */
  DocumentUploaded: 'vvf:native:document-uploaded',
} as const;

export type NativeEventName = (typeof NATIVE_EVENT)[keyof typeof NATIVE_EVENT];

export interface NativeAcceptDetail { selectiveId: string }
export interface NativeRejectDetail { selectiveId: string }
/** Detail di NATIVE_EVENT.TokensSynced (item #5): nuovo access token sincronizzato. */
export interface NativeTokensSyncedDetail { accessToken: string }
/**
 * Detail di NATIVE_EVENT.TokenRefreshFailed: il refresh nativo e' fallito.
 * 'auth' = fallimento definitivo (sessione invalida); 'network' = transitorio.
 */
export interface NativeTokenRefreshFailedDetail { kind: 'auth' | 'network' }
export interface NativeDndResultDetail { granted: boolean }
export interface NativeGpsEnabledDetail { enabled: boolean }
export type GpsTrackingFailureReason = 'no-fg-permission' | 'no-bg-permission' | 'error';
export interface NativeGpsTrackingStatusDetail { active: boolean; reason?: GpsTrackingFailureReason }
export interface NativeVolumesDumpDetail {
  music: number; musicMax: number;
  alarm: number; alarmMax: number;
  notification: number; notificationMax: number;
  /** 0=silent, 1=vibrate, 2=normal */
  ringerMode: number;
  /** 1=granted, 0=missing (impedisce forceRingerNormal) */
  notificationPolicyGranted: number;
}

/** Detail di NATIVE_EVENT.LastBgDump: ultimo snapshot catturato all'arrivo
 *  FCM (anche in background). null se nessuna FCM e' ancora stata ricevuta. */
export interface NativeLastBgDumpDetail {
  /** ISO timestamp della cattura. Null se snapshot mai catturato. */
  ts: string | null;
  /** Dati audio + AppState ('active' | 'background' | 'inactive') o null. */
  data: (NativeVolumesDumpDetail & { appState: string }) | null;
}

// ============================================================================
// Payload selettiva: SignalR + FCM (contratto unificato, P7)
// ============================================================================
// Stesso shape su entrambi i canali. Differenze documentate:
//  - SignalR: lo invia come oggetto nativo (campi typed, array gia' array)
//  - FCM:     lo invia come Dictionary<string,string> (FCM consente solo
//             string values). I client deserializzano i campi non-string
//             via JSON.parse. Il discriminator e' SEMPRE il campo `type`.

/**
 * Tipo discriminante dell'evento selettiva. SOURCE-OF-TRUTH condivisa con il
 * backend C# — i tre valori sono replicati in
 * <c>SelectiveEventTypes.cs</c> (Infrastructure/Notifications). Tenere
 * allineati i due file: i client instradano sul valore di <c>type</c>.
 *
 * Tre segnali con tre comportamenti client DISTINTI:
 *  - Created: nuova selettiva -> sirena, modal di risposta.
 *  - NoLongerNeeded: "intervento non piu' necessario" -> SUONA end-chime
 *    (retry-until-ack) MA la selettiva RESTA attiva e il GPS continua.
 *  - Terminated: "termina intervento" -> cleanup SILENZIOSO (niente suono):
 *    ferma il GPS e rimuove la selettiva dalla lista attiva del responder.
 */
export const SELECTIVE_EVENT_TYPE = {
  Created: 'selective.created',
  NoLongerNeeded: 'selective.no-longer-needed',
  Terminated: 'selective.terminated',
} as const;

export type SelectiveEventType = (typeof SELECTIVE_EVENT_TYPE)[keyof typeof SELECTIVE_EVENT_TYPE];

/**
 * Payload selectiveCreated. Inviato sia da SignalR (event name = 'selectiveCreated')
 * sia da FCM (data.type = 'selective.created').
 */
export interface SelectiveCreatedPayload {
  type: typeof SELECTIVE_EVENT_TYPE.Created;
  selectiveId: string;
  selectiveType: string;
  zone?: string;
  priority?: string;
  callerInfo?: string;
  recipient?: string;
  notes?: string;
  /** userId target (con DND escluso). Vuoto = nessun cliente deve fare audio. */
  targetUserIds: string[];
}

/**
 * Payload "intervento non piu' necessario". Inviato sia da SignalR (event
 * name = 'selectiveNoLongerNeeded') sia da FCM (data.type =
 * 'selective.no-longer-needed'). Stessa shape del Terminated, ma il
 * comportamento client e' diverso: SUONA l'end-chime e la selettiva RESTA
 * attiva (GPS non viene fermato).
 */
export interface SelectiveNoLongerNeededPayload {
  type: typeof SELECTIVE_EVENT_TYPE.NoLongerNeeded;
  selectiveId: string;
  /** userId che hanno risposto Accepted/Arrived — riceveranno l'end-chime. */
  acceptedUserIds: string[];
}

/**
 * Payload selectiveTerminated (cleanup SILENZIOSO). Inviato sia da SignalR
 * (event name = 'selectiveTerminated') sia da FCM (data.type =
 * 'selective.terminated'). Il client ferma il GPS e rimuove la selettiva
 * dalla lista attiva, SENZA suono.
 */
export interface SelectiveTerminatedPayload {
  type: typeof SELECTIVE_EVENT_TYPE.Terminated;
  selectiveId: string;
  /** userId che avevano risposto Accepted/Arrived — devono fare cleanup (stop GPS + rimozione). */
  acceptedUserIds: string[];
}

/**
 * Parser-helper per FCM: i value sono sempre stringhe. acceptedUserIds e
 * targetUserIds arrivano come stringa JSON serializzata. Questo helper
 * normalizza nei tipi del payload.
 */
export function parseSelectiveFcmData(data: Record<string, string>): SelectiveCreatedPayload | SelectiveNoLongerNeededPayload | SelectiveTerminatedPayload | null {
  const type = data['type'];
  const selectiveId = data['selectiveId'];
  if (!type || !selectiveId) return null;

  const parseStringArray = (raw: string | undefined): string[] => {
    if (!raw) return [];
    try {
      const v = JSON.parse(raw);
      return Array.isArray(v) ? v.filter(x => typeof x === 'string') : [];
    } catch { return raw.split(',').filter(Boolean); }
  };

  if (type === SELECTIVE_EVENT_TYPE.Created) {
    return {
      type: SELECTIVE_EVENT_TYPE.Created,
      selectiveId,
      selectiveType: data['selectiveType'] ?? 'Selettiva',
      zone: data['zone'] || undefined,
      priority: data['priority'] || undefined,
      callerInfo: data['callerInfo'] || undefined,
      recipient: data['recipient'] || undefined,
      notes: data['notes'] || undefined,
      targetUserIds: parseStringArray(data['targetUserIds']),
    };
  }
  if (type === SELECTIVE_EVENT_TYPE.NoLongerNeeded) {
    return {
      type: SELECTIVE_EVENT_TYPE.NoLongerNeeded,
      selectiveId,
      acceptedUserIds: parseStringArray(data['acceptedUserIds']),
    };
  }
  if (type === SELECTIVE_EVENT_TYPE.Terminated) {
    return {
      type: SELECTIVE_EVENT_TYPE.Terminated,
      selectiveId,
      acceptedUserIds: parseStringArray(data['acceptedUserIds']),
    };
  }
  return null;
}

// ============================================================================
// Android NotificationChannel IDs
// ============================================================================
// Source of truth condivisa con il backend C# (SelectiveChannelNames.cs).
// IMMUTABILI dopo install: vedi commento in notifications.ts mobile.
export const ANDROID_CHANNEL = {
  /** Channel sirena selettiva nuova (importance MAX, sound=sirena, bypassDnd). */
  SelectiveAlert: 'selective-alerts-v2',
  /** Channel chime fine intervento (importance HIGH, sound=endchime). */
  SelectiveEndChime: 'selective-end-chime-v2',
  /** Channel MUTO selettiva (no sound, solo vibrazione) — usato in
   *  silenzioso/DND e per la selettiva nuova quando suona solo la sirena nativa. */
  SelectiveSilent: 'selective-silent',
  /** Channel generico (importance DEFAULT). */
  Default: 'default',
} as const;

export type AndroidChannelId = (typeof ANDROID_CHANNEL)[keyof typeof ANDROID_CHANNEL];
